/**
 * Stores > Employee purchases at /stores/employee-purchases.
 *
 * Every line of every CounterPoint ticket rung with the EMPLOYEE discount, so
 * HQ can review what staff bought, who rang it, and how it was paid. One row
 * per ticket line. Gated by its own stores.employeepurchases slug.
 *
 * Standard report behavior, matching Item Sales: date range, store filter,
 * search, server-side sort through SortableHeader (encoded in the URL as
 * `sort=<column>:<dir>`), infinite scroll through DataGridShell, totals for
 * every matching line in the footer, and an Excel download that follows the
 * filters and sort on screen. Filters live in the URL so a link survives
 * refresh.
 *
 * Two data facts the page states rather than hides:
 *   - Most employee tickets are rung to the generic walk-in customer, so
 *     CounterPoint records no buyer. Those rows read "Not recorded"; the
 *     "Rung up by" column (the register login) is always filled.
 *   - Discount detail reaches us with the nightly copy, so the newest day is
 *     normally yesterday. The subtitle names the newest day present.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, Download, Loader2 } from 'lucide-react';
import { InfoTooltip, SortableHeader, gridTheadClassName, type SortDir } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { FilterLabel } from '@/components/FilterLabel';
import { ClearableSearchInput } from '@/components/ClearableSearchInput';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Pill } from '@/components/Pill';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { useRetailStores } from '@/lib/useRetailStores';
import { formatDate } from '@/lib/dateUtils';
import { useReportPageState } from '@/lib/reportPageState';
import { denverToday, isNotAvailable, storeLabel } from '@/lib/useStores';
import {
  EMPLOYEE_PURCHASES_DEFAULT_DAYS,
  EMPLOYEE_PURCHASES_SORT_OPTIONS,
  downloadEmployeePurchasesXlsx,
  employeePurchasesRangeProblem,
  formatEmployeePurchasesSort,
  formatMoney,
  formatQty,
  formatRegisterTime,
  parseEmployeePurchasesSort,
  useEmployeePurchases,
  type EmployeePurchaseLine,
  type EmployeePurchasesFilters,
  type EmployeePurchasesSortColumn,
} from '@/lib/useEmployeePurchases';
import { AccessDeniedCard, ErrorCard, NotAvailableCard, StoresHeader } from './storesShared';

const SLUG = 'stores.employeepurchases';
const COLUMN_COUNT = 10;

const inputCls =
  'border border-amp-line rounded px-2 py-1.5 text-xs bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink';
const btnCls =
  'inline-flex items-center gap-1 rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm';
const ink = 'text-amp-ink dark:text-amp-dark-ink';
const muted = 'text-amp-ink-muted dark:text-amp-dark-ink-muted';

const AMBER_PILL = 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
const RED_PILL = 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
const GREY_PILL = 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';

function shiftDate(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function EmptyBody({ message }: { message: string }) {
  return (
    <tr>
      <td colSpan={COLUMN_COUNT} className={`px-4 py-6 ${muted}`}>
        {message}
      </td>
    </tr>
  );
}

/** The things worth a second look on an employee sale, as pills. */
function Flags({ r }: { r: EmployeePurchaseLine }) {
  const pills: Array<{ label: string; tone: string; title: string }> = [];
  if (r.is_return) pills.push({ label: 'Return', tone: RED_PILL, title: 'This line is a return.' });
  if (r.price_override)
    pills.push({ label: 'Price override', tone: RED_PILL, title: 'The cashier typed in this price by hand.' });
  if (r.other_discount !== 0)
    pills.push({
      label: 'Extra discount',
      tone: AMBER_PILL,
      title: `This item also got a second discount on top of the employee discount: ${r.other_discount_codes ?? 'unnamed'} (${formatMoney(r.other_discount)}).`,
    });
  if (r.paid_cash) pills.push({ label: 'Cash', tone: GREY_PILL, title: `Paid with: ${r.pay_types ?? 'cash'}.` });
  if (pills.length === 0) return <span className={muted}>--</span>;
  return (
    <div className="flex flex-col items-start gap-1">
      {pills.map((p) => (
        <span key={p.label} title={p.title}>
          <Pill tone={p.tone}>{p.label}</Pill>
        </span>
      ))}
    </div>
  );
}

export function EmployeePurchases() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, SLUG, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const today = denverToday();
  const defaultFrom = shiftDate(today, -(EMPLOYEE_PURCHASES_DEFAULT_DAYS - 1));
  const from = searchParams.get('from') || defaultFrom;
  const to = searchParams.get('to') || today;
  const q = searchParams.get('q') ?? '';
  const flagged = searchParams.get('flagged') === '1';
  const storeNos = useMemo(() => searchParams.getAll('store_no'), [searchParams]);
  const sort = useMemo(() => parseEmployeePurchasesSort(searchParams.get('sort')), [searchParams]);

  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const update = useCallback(
    (mutate: (sp: URLSearchParams) => void) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  // Defaults stay out of the URL.
  const setFrom = (v: string) => update((sp) => (v && v !== defaultFrom ? sp.set('from', v) : sp.delete('from')));
  const setTo = (v: string) => update((sp) => (v && v !== today ? sp.set('to', v) : sp.delete('to')));
  const setQ = (v: string) => update((sp) => (v ? sp.set('q', v) : sp.delete('q')));
  const setFlagged = (v: boolean) => update((sp) => (v ? sp.set('flagged', '1') : sp.delete('flagged')));
  const setStores = (next: string[]) =>
    update((sp) => {
      sp.delete('store_no');
      for (const v of next) sp.append('store_no', v);
    });

  const toggleSort = (col: EmployeePurchasesSortColumn) => {
    if (!sort || sort.column !== col) update((sp) => sp.set('sort', `${col}:asc`));
    else if (sort.dir === 'asc') update((sp) => sp.set('sort', `${col}:desc`));
    else update((sp) => sp.delete('sort'));
  };
  const sortedFor = (col: EmployeePurchasesSortColumn): SortDir | null =>
    sort && sort.column === col ? sort.dir : null;
  const setSortColumn = (col: string) =>
    update((sp) => {
      if (!col) sp.delete('sort');
      else sp.set('sort', formatEmployeePurchasesSort({ column: col as EmployeePurchasesSortColumn, dir: sort?.dir ?? 'asc' }));
    });
  const flipSortDir = () => {
    if (!sort) return;
    update((sp) => sp.set('sort', formatEmployeePurchasesSort({ column: sort.column, dir: sort.dir === 'asc' ? 'desc' : 'asc' })));
  };

  const problem = employeePurchasesRangeProblem(from, to);
  const filters: EmployeePurchasesFilters = useMemo(
    () => ({ from, to, store_no: storeNos, q, flagged }),
    [from, to, storeNos, q, flagged],
  );
  const dataQ = useEmployeePurchases(filters, sort, canView && problem === null);

  const storesQ = useRetailStores();
  const storeOptions = useMemo(
    () =>
      [...storesQ.stores]
        .sort((a, b) => Number(a.store_no) - Number(b.store_no))
        .map((s) => ({ value: String(s.store_no), label: storeLabel(s.store_no, s.store_name) })),
    [storesQ.stores],
  );

  const storeLabels = useMemo(() => {
    const byValue = new Map(storeOptions.map((o) => [o.value, o.label]));
    return storeNos.map((v) => byValue.get(v) ?? v);
  }, [storeNos, storeOptions]);
  const pageState = useMemo(
    () => [
      { label: 'Dates', value: `${from} to ${to}` },
      { label: 'Stores', value: storeLabels.length > 0 ? storeLabels.join(', ') : 'All stores' },
      ...(q ? [{ label: 'Search', value: q }] : []),
      ...(flagged ? [{ label: 'Only flagged', value: 'Yes' }] : []),
      ...(sort ? [{ label: 'Sort', value: `${sort.column} ${sort.dir}` }] : []),
    ],
    [from, to, storeLabels, q, flagged, sort],
  );
  useReportPageState(pageState);

  async function handleDownload() {
    setExporting(true);
    setToast(null);
    try {
      await downloadEmployeePurchasesXlsx(filters, sort);
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setExporting(false);
    }
  }

  if (user && !canView) return <AccessDeniedCard what="Employee purchases" />;

  const asOfNote =
    dataQ.asOf === undefined
      ? ''
      : dataQ.asOf === null
        ? ' No employee purchases in this range yet.'
        : ` Newest sales day in this range: ${formatDate(dataQ.asOf)}.`;

  const header = (
    <StoresHeader
      title="Employee purchases"
      subtitle={`Every item rung with the employee discount: who rang it, what it was, and how it was paid. Register discounts reach Cockpit overnight, so the newest day is normally yesterday.${asOfNote}`}
      actions={<ToolPinToggle toolSlug="stores-employee-purchases" toolName="Employee purchases" />}
    />
  );

  if (isNotAvailable(dataQ.error)) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        {header}
        <NotAvailableCard what="Employee purchases" />
      </div>
    );
  }

  const hasFilters = storeNos.length > 0 || Boolean(q) || flagged || Boolean(sort);
  const activeFilterCount = (storeNos.length > 0 ? 1 : 0) + (q ? 1 : 0) + (flagged ? 1 : 0);

  const filterBar = (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <FilterLabel htmlFor="ep-from">From</FilterLabel>
        <input id="ep-from" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className={inputCls} />
      </div>
      <div>
        <FilterLabel htmlFor="ep-to">To</FilterLabel>
        <input id="ep-to" type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} className={inputCls} />
      </div>
      <div>
        <FilterLabel as="div">Store</FilterLabel>
        <SearchableSelect
          options={storeOptions}
          selected={storeNos}
          onChange={setStores}
          placeholder="All stores"
          ariaLabel="Filter by store"
        />
      </div>
      <div className="min-w-[220px] flex-1">
        <FilterLabel htmlFor="ep-q">Search</FilterLabel>
        <ClearableSearchInput
          id="ep-q"
          value={q}
          onChange={setQ}
          placeholder="Employee, cashier, ticket, SKU, or item..."
          className="w-full border border-amp-line rounded pl-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
        />
      </div>
      <label htmlFor="ep-flagged" className={`flex items-center gap-2 pb-1.5 text-sm ${ink}`}>
        <input id="ep-flagged" type="checkbox" checked={flagged} onChange={(e) => setFlagged(e.target.checked)} />
        Only flagged
        <InfoTooltip label="what counts as flagged">
          A line is flagged when it is a return, when the cashier typed in the price by hand, or when the item
          also got a second discount on top of the employee discount, such as a clearance markdown.
        </InfoTooltip>
      </label>
      <button
        type="button"
        onClick={() => void handleDownload()}
        disabled={exporting || problem !== null}
        title="Download these rows as Excel. The workbook follows the date range, filters, and sort on screen."
        className={btnCls}
      >
        {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
        Download
      </button>
      {hasFilters && (
        <button
          type="button"
          onClick={() => update((sp) => ['q', 'store_no', 'flagged', 'sort'].forEach((k) => sp.delete(k)))}
          className={btnCls}
        >
          Reset filters
        </button>
      )}
      {problem && <p className="pb-1.5 text-sm text-red-700 dark:text-red-300">{problem}</p>}
    </div>
  );

  const { totals } = dataQ;
  const totalsFooter = (
    <div className={`flex flex-wrap items-center gap-x-6 gap-y-1 text-[12px] ${ink}`}>
      <span className="font-semibold">Totals for every matching line</span>
      <span>
        Tickets <span className="font-semibold">{totals.tickets.toLocaleString('en-US')}</span>
      </span>
      <span>
        Qty <span className="font-semibold">{formatQty(totals.qty)}</span>
      </span>
      <span>
        Sub-total <span className="font-semibold">{formatMoney(totals.subtotal)}</span>
      </span>
      <span>
        Employee discount <span className="font-semibold">{formatMoney(totals.employee_discount)}</span>
      </span>
      <span>
        Other discounts <span className="font-semibold">{formatMoney(totals.other_discount)}</span>
      </span>
      <span>
        Total <span className="font-semibold">{formatMoney(totals.total)}</span>
      </span>
    </div>
  );

  const sortControl = (
    <div className="flex items-center gap-1">
      <span className={`text-[12px] ${muted}`}>Sort</span>
      <select
        aria-label="Sort rows by"
        value={sort?.column ?? ''}
        onChange={(e) => setSortColumn(e.target.value)}
        className={inputCls}
      >
        <option value="">Newest first</option>
        {EMPLOYEE_PURCHASES_SORT_OPTIONS.map((o) => (
          <option key={o.column} value={o.column}>
            {o.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={flipSortDir}
        disabled={!sort}
        aria-label={sort?.dir === 'desc' ? 'Sort ascending instead' : 'Sort descending instead'}
        title={sort ? 'Reverse the order.' : 'Pick a column to sort by first.'}
        className="rounded border border-amp-line p-1.5 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
      >
        {sort?.dir === 'desc' ? <ArrowDown className="h-3 w-3" aria-hidden /> : <ArrowUp className="h-3 w-3" aria-hidden />}
      </button>
    </div>
  );

  // Ten compact columns so the whole line fits a laptop screen without
  // scrolling sideways (Steven, 2026-10-29). Related fields share a cell:
  // date over store, cashier over employee, ticket over register and payment.
  const th = 'px-2 py-2';
  const td = 'px-2 py-2 align-top';
  const sub = `mt-0.5 text-[12px] ${muted}`;
  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}

      {toast && (
        <div
          className="mb-4 flex shrink-0 items-center justify-between rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/40 dark:text-red-200"
          role="status"
        >
          {toast}
          <button type="button" onClick={() => setToast(null)} className="ml-4 text-xs">
            &times;
          </button>
        </div>
      )}

      <DataGridShell
        total={dataQ.totalCount}
        loaded={dataQ.rows.length}
        countNoun="lines"
        toolbar={filterBar}
        activeFilterCount={activeFilterCount}
        mobileActions={sortControl}
        footer={totalsFooter}
        onReachEnd={dataQ.fetchNextPage}
        hasMore={dataQ.hasNextPage}
        isLoadingMore={dataQ.isFetchingNextPage}
      >
        <table className="w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <SortableHeader
                label="Date / store"
                sorted={sortedFor('date_time')}
                onToggle={() => toggleSort('date_time')}
                thClassName={th}
                tooltip="Register time at the store, in the store's own time zone, with the store underneath."
              />
              <SortableHeader
                label="Rung up by"
                sorted={sortedFor('cashier')}
                onToggle={() => toggleSort('cashier')}
                thClassName={th}
                tooltip="The register login that rang the sale. The employee who bought is shown underneath when a customer record was on the ticket; most employee sales are rung to the walk-in customer, so it reads Employee not recorded."
              />
              <SortableHeader
                label="Ticket"
                sorted={sortedFor('ticket')}
                onToggle={() => toggleSort('ticket')}
                thClassName={th}
                tooltip="The register ticket number, with the register and how the ticket was paid underneath."
              />
              <SortableHeader label="Line item" sorted={sortedFor('item')} onToggle={() => toggleSort('item')} thClassName={th} />
              <SortableHeader
                label="Price"
                sorted={sortedFor('price')}
                onToggle={() => toggleSort('price')}
                align="right"
                thClassName={th}
                tooltip="Sale price per unit before discounts. Candy sold by weight is priced per pound."
              />
              <SortableHeader label="Qty" sorted={sortedFor('qty')} onToggle={() => toggleSort('qty')} align="right" thClassName={th} />
              <SortableHeader
                label="Sub-total"
                sorted={sortedFor('subtotal')}
                onToggle={() => toggleSort('subtotal')}
                align="right"
                thClassName={th}
                tooltip="The line before any discount, before tax."
              />
              <SortableHeader
                label="Discount"
                sorted={sortedFor('discount')}
                onToggle={() => toggleSort('discount')}
                align="right"
                thClassName={th}
                tooltip="Every discount on the line. When the item also got a second discount on top of the employee discount, the two amounts show underneath."
              />
              <SortableHeader
                label="Total"
                sorted={sortedFor('total')}
                onToggle={() => toggleSort('total')}
                align="right"
                thClassName={th}
                tooltip="What the line sold for, before tax. Returns show as negative amounts."
              />
              <th className={`${th} text-left font-medium`}>Flags</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {problem ? (
              <EmptyBody message={problem} />
            ) : dataQ.isLoading ? (
              <EmptyBody message="Loading employee purchases..." />
            ) : dataQ.error ? (
              <tr>
                <td colSpan={COLUMN_COUNT} className="px-4 py-6">
                  <ErrorCard message={dataQ.error.message} />
                </td>
              </tr>
            ) : dataQ.rows.length === 0 ? (
              <EmptyBody message={flagged ? 'No flagged lines match these filters.' : 'No employee purchases match these filters.'} />
            ) : (
              dataQ.rows.map((r) => (
                <tr key={`${r.doc_id}:${r.line_no}`} className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
                  <td className={`${td} ${ink}`}>
                    <div className="whitespace-nowrap">{formatRegisterTime(r.date_time, r.bus_date)}</div>
                    <div className={sub}>{storeLabel(r.store_no, r.store_name ?? '')}</div>
                  </td>
                  <td className={`${td} ${ink}`}>
                    <div>{r.cashier_name ?? r.cashier_id ?? '--'}</div>
                    <div className={sub} title={r.buyer_name ? undefined : 'Rung to the walk-in customer, so CounterPoint recorded no buyer.'}>
                      {r.buyer_name ? `Employee: ${r.buyer_name}` : 'Employee not recorded'}
                    </div>
                  </td>
                  <td className={`${td} ${ink}`}>
                    <div className="whitespace-nowrap font-mono text-xs">{r.ticket_no}</div>
                    <div className={sub}>
                      {[r.station ? `Reg ${r.station}` : null, r.pay_types].filter(Boolean).join(', ') || '--'}
                    </div>
                  </td>
                  <td className={`${td} ${ink}`}>
                    <div>{r.description ?? '--'}</div>
                    {r.item_no && <div className={`${sub} font-mono`}>{r.item_no}</div>}
                  </td>
                  <td className={`${td} text-right whitespace-nowrap ${ink}`}>{formatMoney(r.unit_price)}</td>
                  <td className={`${td} text-right whitespace-nowrap ${ink}`}>{formatQty(r.qty)}</td>
                  <td className={`${td} text-right whitespace-nowrap ${ink}`}>{formatMoney(r.subtotal)}</td>
                  <td className={`${td} text-right whitespace-nowrap ${ink}`}>
                    <div>{formatMoney(r.discount)}</div>
                    {r.other_discount !== 0 && (
                      <>
                        <div className={sub}>Employee {formatMoney(r.employee_discount)}</div>
                        <div className={sub}>Other {formatMoney(r.other_discount)}</div>
                      </>
                    )}
                  </td>
                  <td className={`${td} text-right whitespace-nowrap font-medium ${ink}`}>{formatMoney(r.total)}</td>
                  <td className={td}>
                    <Flags r={r} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}
